import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, BookOpen, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { partnerInstitutions, programCategories } from '../data/mock';
import * as LucideIcons from 'lucide-react';

export const ProgramsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/programs?search=${encodeURIComponent(searchQuery)}`);
    } else {
      navigate('/programs');
    }
  };

  const handleCategoryClick = (category) => {
    navigate('/programs', { state: { category: category.category } });
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="programs" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-blue-600 font-semibold text-sm uppercase tracking-wide">
            Programs & Institutions
          </span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-bold text-gray-900">
            Explore Our Programs
          </h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Find the perfect program at our partner colleges across British Columbia.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-16">
          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input 
              type="text"
              placeholder="Search programs, colleges, or careers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 pr-24 py-6 text-lg rounded-xl border-2 border-gray-200 focus:border-blue-500 transition-colors duration-200"
            />
            <Button 
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700"
            >
              Search
            </Button>
          </form>
        </div>

        {/* Program Categories */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Browse by Category
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {programCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category)}
                className="group bg-gray-50 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-500 rounded-xl p-6 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-blue-600 mb-4 mx-auto group-hover:scale-110 transition-transform duration-200">
                  {getIcon(category.icon)}
                </div>
                <h4 className="font-semibold text-gray-900 text-sm mb-2 text-center">
                  {category.category}
                </h4>
                <p className="text-xs text-gray-600 text-center">
                  {category.programCount} programs
                </p>
              </button>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button 
              onClick={() => navigate('/programs')}
              variant="outline"
              size="lg"
              className="border-2 border-gray-300 hover:border-blue-600 hover:text-blue-600"
            >
              View All Programs
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Partner Institutions Section */}
        <div id="partners" className="mt-24">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Our Partner Institutions
            </h3>
            <p className="text-lg text-gray-600">
              Gain access to top colleges across Canada
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnerInstitutions.map((institution) => {
              // Use the slug directly from the institution object
              const slug = institution.slug;
              
              return (
                <div 
                  key={institution.id}
                  className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-500 hover:shadow-xl transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden bg-gray-100">
                    <img 
                      src={institution.image}
                      alt={`${institution.name} campus — ${institution.specialization} programs in ${institution.location}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h4 className="text-white font-bold text-lg">
                        {institution.name}
                      </h4>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center text-gray-600 mb-3">
                      <MapPin className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-sm">{institution.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600 mb-3">
                      <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                      <span className="text-sm font-medium">{institution.specialization}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">{institution.programs}+ Programs Available</p>
                    <Button 
                      onClick={() => navigate(`/institutions/${slug}`)}
                      variant="outline" 
                      className="w-full group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all duration-300"
                    >
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
